import React from 'react';
import { useConsent } from '../contexts/ConsentContext';

interface ConsentGateProps {
  /** Short name of the embedded service, e.g. "Google Maps". */
  title: string;
  /** Label of the activate button. */
  buttonLabel?: string;
  /** The actual embed (iframe etc.), rendered only after consent. */
  children: React.ReactNode;
}

/**
 * Blocks a third-party embed until the visitor consents. The placeholder is
 * intentionally minimal — the explanatory text and the actual choice live in
 * the global ConsentBanner, which "Aktivieren" re-opens. Nothing is loaded
 * from the third party until global consent is granted.
 *
 * Must sit inside a positioned, sized container (fills it via position:absolute).
 */
const ConsentGate: React.FC<ConsentGateProps> = ({ title, buttonLabel = 'Aktivieren', children }) => {
  const { externalMedia, reopen } = useConsent();

  if (externalMedia === 'granted') {
    return <>{children}</>;
  }

  return (
    <div
      role="group"
      aria-label={`${title} – externe Inhalte deaktiviert`}
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.9rem',
        textAlign: 'center',
        padding: '1.5rem',
        background: 'linear-gradient(135deg, #f3f2f0 0%, #e6e4e1 100%)',
        color: 'var(--color-text, #111)',
      }}
    >
      <svg
        width="30"
        height="30"
        viewBox="0 0 24 24"
        fill="none"
        stroke="var(--color-text-secondary, #777)"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
        <circle cx="12" cy="10" r="3" />
      </svg>

      <span style={{ fontSize: '0.9rem', color: 'var(--color-text-secondary, #555)' }}>{title}</span>

      <button
        type="button"
        onClick={reopen}
        style={{
          padding: '0.55rem 1.4rem',
          fontSize: '0.85rem',
          fontWeight: 600,
          color: '#fff',
          background: 'var(--color-brand, #FC5000)',
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer',
        }}
      >
        {buttonLabel}
      </button>
    </div>
  );
};

export default ConsentGate;
